// src/components/intake/intakeCopy.js
//
// Every string the SP4 screens show (spec decision 14): You’re in, the seven
// question pages, the dashboard’s Your questions pass and answer cards, the
// Questions page and the staff view, and the rail’s Choose your plan entry.
// Screens read strings by key; tests look them up here. The ONE test that
// repeats the literals is tests/components/intake-copy.test.js.
//
// Prototype copy is not approved: every string marked `// Wording pending`
// goes to Astra in one review pass (spec § Design truth). Per-page strings
// are keyed by the server’s page key (decision 7), never by position, so a
// reorder of INTAKE_PAGES needs no edit here.
//
// Not here, because they are data the server serves: the option strings,
// Q7’s five prompts (the seeded options of its question) and the Plaid
// consent row’s label and why (the consent catalog).
//
// Apostrophes are curly (U+2019) as the spec and prototype print them (R206),
// unlike onboardingCopy.js. `—` is U+2014,
// `–` U+2013, `…` U+2026, `·` U+00B7.

// ---- Counts (decision 15: the count is computed, never written) --------------
const COUNT_WORDS = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
];
export const countWord = (n) => COUNT_WORDS[n] ?? String(n);
// A missing or invalid count drops the number rather than print a non-word.
const shortCount = (n, noun) =>
  Number.isInteger(n) && n > 0 ? `${countWord(n)} short ${noun}` : `Short ${noun}`;

// ---- You’re in (/welcome) ----------------------------------------------------
export const YOURE_IN_H1 = (firstName) =>
  firstName ? `You’re in, ${firstName}` : "You’re in";
// Wording pending (plan and price lines are SP5’s to make live)
export const YOURE_IN_INTRO =
  "Every plan is free for your first month, and the $99 setup fee is waived. You can cancel any time before your first charge.";
export const YOURE_IN_PASS_KIND = "Your first month";
export const YOURE_IN_PASS_BIG = "Free, on every plan";
export const YOURE_IN_SETUP_FEE = "Setup fee";
export const YOURE_IN_SETUP_FEE_VALUE = "Waived";
export const YOURE_IN_FIRST_CHARGE = "First charge";
export const YOURE_IN_FIRST_CHARGE_VALUE = "After your free month";
export const YOURE_IN_NEXT_HEADING = "What happens next";
export const YOURE_IN_NEXT_QUESTIONS = (n) => shortCount(n, "questions");
// Wording pending ("About five minutes" vs the dashboard’s "3–5 minutes")
export const YOURE_IN_NEXT_QUESTIONS_BODY =
  "About you and your money, so the first advisor who replies already knows where to start. About five minutes, and you can save and finish later.";
// Wording pending
export const YOURE_IN_NEXT_PLAN = "Pick the plan that fits";
// Wording pending
export const YOURE_IN_NEXT_PLAN_BODY =
  "Then add a card for after your free month. Nothing is charged today.";
// Wording pending (variant A: the thread opens after payment)
export const YOURE_IN_NEXT_THREAD = "Your advisors’ thread opens";
// Wording pending
export const YOURE_IN_NEXT_THREAD_BODY =
  "Once you’ve chosen a plan and added a card. Your Astra advisors typically respond within 1 business day.";
export const YOURE_IN_START = "Start the questions";
// Wording pending
export const YOURE_IN_SKIP = "Skip the questions for now";

// ---- The question pages (/intake/:pageKey) ----------------------------------
export const INTRO_H2 = "Let’s get to know your goals.";
export const INTRO_BODY =
  "Answer a few quick questions to help your advisor understand what matters most to you. Share whatever you can — skip anything you’re unsure about. Your advisor will help fill in the rest.";
export const STEPS_LABEL = "Onboarding progress";
export const STEP_DONE = ", done";
export const QUESTION_N_OF = (n, total) => `Question ${n} of ${total}`;
// The side step list (decision 21).
export const STEP_LABELS = {
  q1_priorities: "Priority",
  q2_goals: "Your Goals",
  q3_planning: "Planning Support",
  q4_big_decisions: "Big Decisions",
  q5_debts: "Debts",
  q6_investments: "Investments",
  q7_about_you: "About You",
};
// Back and primary name their destination (spec § The seven pages).
// Wording pending ("Money Snapshot (Debts)" vs the step list’s "Debts")
export const NAV_LABELS = {
  q1_priorities: "Priority",
  q2_goals: "Your Goals",
  q3_planning: "Planning Support",
  q4_big_decisions: "Big Decisions",
  q5_debts: "Money Snapshot (Debts)",
  q6_investments: "Money Snapshot (Investments)",
  q7_about_you: "About You",
};
export const BACK_TO_YOURE_IN = "You’re in";
export const CHOOSE_YOUR_PLAN = "Choose your plan";
export const CONTINUE_LATER = "Continue later";
// Wording pending (prototype copy)
export const PAGE_TITLES = {
  q1_priorities: "What do you want help with first?",
  q2_goals: "Which areas do you want clarity or help with?",
  q3_planning: "What kind of planning support would be helpful?",
  q4_big_decisions: "Anything coming up that might affect your money or taxes?",
  q5_debts: "Want to give your advisor a quick snapshot of your money?",
  q6_investments: "Want to give your advisor a quick snapshot of your money?",
  q7_about_you: "Let’s help your advisor understand your world.",
};
// Wording pending (prototype copy)
export const PAGE_LEDES = {
  q1_priorities:
    "This helps your advisor focus on what matters most to you right now.",
  q2_goals:
    "Pick as many as you want — helps your advisor understand your priorities.",
  q3_planning:
    "No fancy terms — just choose what fits your life. There are two lists, one for the next few years and one for further out. Choose all that apply from either.",
  q4_big_decisions: "Optional — choose what applies.",
  q5_debts:
    "Totally optional. Estimates are great. Your advisor can help fill this in later over chat.",
  q6_investments:
    "Totally optional. Estimates are great. Your advisor can help fill this in later over chat.",
  q7_about_you:
    "Share as much or as little as you want — this helps your advisor personalize your plan.",
};
// The status line while Next is disabled, by the page’s kind.
// Keyed by the server’s statusKey names (400 not_answered carries the same).
export const STATUS_NEED = {
  pickAtLeastOne: "Pick at least one to continue",
  addAnAmount: "Add an amount for at least one to continue",
  answerAtLeastOne: "Answer at least one question to continue",
};
export const STATUS_OR = ", or ";
export const STATUS_SKIP = "skip this question";
export const LINE_ERROR =
  "Please enter a value for selected options or unselect.";
// Wording pending
export const SKIP_TITLE = "Skip this question?";
// Wording pending
export const SKIP_BODY =
  "If you skip this question, we may not have enough information to be fully prepared for the meeting. Would you like to confirm that you still want to skip this question?";
// Wording pending
export const SKIP_CANCEL = "Cancel";
// Wording pending
export const SKIP_CONFIRM = "Skip question";
// Wording pending
export const SAVE_FAILED = "We couldn’t save just now. Try again.";
// Wording pending
export const ANSWERS_LOAD_FAILED = "Couldn’t load your answers. Try again.";

// Q1, ranking (decision 28)
// Wording pending
export const RANK_INSTRUCTION =
  "Tap in the order that matters most to rank your top 3";
export const RANKED_LIST_LABEL = "Your top 3, in order";
export const RANKED_SLOT = (n, name) => `${n}. ${name || "—"}`;
export const RANKED_AS = (n) => `, ranked ${n}`;
// Q1, Q2
export const CLICK_ALL = "Click all that apply";
export const SOMETHING_ELSE = "Something else";
// Q3, two numbered parts
export const PART_OF = (n, total) => `· part ${n} of ${total}`;
export const Q3_PARTS = {
  shortTerm: {
    heading: "Short-term",
    lede: "The next 1–5 years: bills, budgets, emergency savings, credit.",
    other: "Other (short-term)",
    read: "Short-term planning",
  },
  longTerm: {
    heading: "Long-term",
    lede: "Five years and beyond: retirement, a home, kids’ education, estate.",
    other: "Other (long-term)",
    read: "Long-term planning",
  },
};
export const SELECT_ALL = "Select all";
export const SELECT_ALL_NAMED = (part) => `Select all, ${part}`;
// Q4
export const Q4_PLANS_LABEL =
  "What are you planning to buy, and about how much?";
export const Q4_PLANS_PLACEHOLDER = "e.g., car, ~$20k";
// Q5, Q6
export const AMOUNT_PLACEHOLDER = {
  q5_debts: "Balance owed",
  q6_investments: "Amount",
};
export const AMOUNT_LABEL = (name, pageKey) =>
  `${name}, ${AMOUNT_PLACEHOLDER[pageKey].toLowerCase()}`;
export const MANUAL_HEADING = "Add your accounts manually";
export const MANUAL_TOTAL = { q5_debts: "Total owed", q6_investments: "Total" };
export const MANUAL_LEDE =
  "No need to be exact — estimates are perfectly fine. Your advisor will help you fill in the details.";
export const PLAID_HEADING = "Fill with Plaid";
// Wording pending
export const PLAID_LEDE =
  "Link an account and Plaid fills these in for you, once. You can still change any of them.";
// Wording pending
export const PLAID_UNSURE =
  "If you’re unsure, skip this step. Your advisor will walk through it with you.";
export const PLAID_BUTTON = {
  q5_debts: "Connect bank account with Plaid",
  q6_investments: "Connect investment accounts with Plaid",
};
// Wording pending (a suggestion for a line that already has a typed amount)
export const USE_THIS = "Use this";
export const SUGGESTED = (amount) => `Plaid suggests $${amount}`;
// Wording pending
export const PLAID_ERROR =
  "We couldn’t reach your bank just now. You can type the amounts instead.";
// Wording pending (Astra): the Questions page edit view, above the lines, when
// linked accounts filled or suggested at least one
export const PLAID_SUGGEST_NOTE =
  "Figures from your linked accounts are filled in below. Check them, then save.";
// Q7
export const YES = "Yes";
export const NO = "No";
// Wording pending (prototype placeholders)
export const Q7_PLACEHOLDERS = {
  rely: "Online research, partner, podcasts, gut instinct…",
  life: "Anything that shapes your priorities.",
  nonFinancial: "Share anything you want your advisor to keep in mind.",
  talkFirst: "Investing, debt, retirement, buying a home…",
};

// ---- Dashboard while the stage is intake (/dashboard) ---------------------------
export const DASH_H1 = (firstName) => (firstName ? `Hi, ${firstName}` : "Hi");
export const PASS_KIND = "Your questions";
export const PASS_BIG = "Help us know you better";
// Wording pending ("questions" vs the receipt’s "pages")
export const PASS_SUB = (n) =>
  `${shortCount(n, "questions")} about you and your money, so the first advisor who replies already knows where to start.`;
export const PASS_PROGRESS_LABEL = "Progress";
export const PASS_PROGRESS = (answered, total) =>
  `${answered} of ${total} answered`;
export const PASS_START = "Start the questions";
export const PASS_PICK_UP = "Pick up where you left off";
export const DASH_ANSWERS_HEADING = "Your answers so far";
// Wording pending (the cards the prototype does not draw)
export const CARD_TITLES = {
  q1_priorities: "Top priorities",
  q2_goals: "Financial goals",
  q3_planning: "Planning support",
  q4_big_decisions: "Big decisions",
  q5_debts: "Debts",
  q6_investments: "Investments",
  q7_about_you: "About you",
};
export const RANKED_PILL = (n, name) => `${n} · ${name}`;
export const CARD_EDIT = "Edit";
export const CARD_EDIT_NAMED = (title) => `Edit ${title}`;
export const AMOUNT_TOTAL = "Total";

// ---- Questions page (/questionnaire) and the staff view ------------------------
export const QP_H1 = "Questionnaire";
// Wording pending
export const QP_INTRO =
  "Your answers, in one place. Edit any of them here; your advisors see the changes.";
export const QP_STAFF_TITLE = "Questionnaire answers";
export const QP_ITEM_TITLES = {
  q1_priorities: "What do you want help with first?",
  q2_goals: "Which areas do you want clarity or help with?",
  q3_planning: "What kind of planning support would be helpful?",
  q4_big_decisions: "Anything coming up that might affect your money or taxes?",
  q5_debts: "Debts",
  q6_investments: "Investments",
  q7_about_you: "Let’s help your advisor understand your world.",
};
export const QP_NA = "N/A";
export const QP_EDIT = "Edit";
export const QP_EDIT_NAMED = (title) => `Edit: ${title}`;
export const QP_SAVE = "Save changes";
export const QP_CANCEL = "Cancel";
export const QP_SAVED = "Saved";
export const QP_CLEARED = "Cleared";
export const QP_PLAID_LINK_ANOTHER = "Link another account";
export const QP_PLAID_LINK = "Link an account with Plaid";
// SP6 (spec § Screens per app → Web, the Questions page row): the bank names
// as drawn ("Linked through Plaid: Chase, Fidelity"); from four banks on, the
// first three "and N more" (proposed, Wording pending).
export const QP_PLAID_NAMES = (names) =>
  names.length === 0
    ? "Linked through Plaid"
    : `Linked through Plaid: ${
        names.length > 3
          ? `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`
          : names.join(", ")
      }`;
export const QP_PLAID_MANAGE = "Manage linked accounts";
// Wording pending: one bank linked names it; with more, the intake GET does
// not say which (R321).
export const QP_PLAID_ATTENTION = (name) =>
  `${name} needs you to sign in again.`;
// Wording pending
export const QP_PLAID_ATTENTION_ANY =
  "One of your banks needs you to sign in again.";

// ---- The rail for an unpaid client (decision 29, variant A) --------------------
export const RAIL_CHOOSE_PLAN = "Choose your plan";
